<template>
  <div v-if="isMounted" :class="classes" :style="{ backgroundColor, ...styles }">
    <div class="base-steps--container">
      <div class="base-steps--holder">
        <div
          ref="steps"
          class="base-steps--steps"
        >
          <span v-if="scroll" class="base-steps--slider" />
          <template v-for="(item, inx) in items">
            <div
              :key="item.value"
              :ref="item.value"
              :class="[
                'base-steps--step',
                value === item.value
                  ? 'active'
                  : inx < currentPos
                    ? 'previous'
                    : inx > currentPos + 1
                      ? 'out-of-reach'
                      : '',
                errors[inx] === false
                  ? 'error'
                  : '',
              ]"
              @click="update(item.value)"
            >
              <span v-if="errors[inx] === false" class="step">
                <span>
                  <i class="fas fa-times" />
                </span>
              </span>
              <span v-else-if="inx < currentPos" class="step">
                <span>
                  <i class="fas fa-pen" />
                </span>
              </span>
              <span v-else class="step">{{ inx + 1 }}</span>
              <span>{{ item.label }}</span>
            </div>
            <div v-if="inx < items.length - 1" :key="inx" class="divider base-steps--step" />
          </template>
        </div>
      </div>
    </div>
    <div class="base-steps--content">
      <div
        v-for="item in items"
        :key="item.value"
        :class="['base-steps--content-holder', item.value === value ? 'active' : '']"
      >
        <div
          v-if="!solo"
          :ref="item.value"
          class="base-steps--content-item"
        >
          <slot :name="item.value" />
        </div>
      </div>
    </div>
    <div class="base-steps--actions">
      <BaseButton
        outlined
        rounded
        highlight
        size="xs"
        background-color="#fff"
        fill
        @click="update(nextStep)"
      >
        {{ lastStep ? 'Speichern' : 'Weiter' }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseStepper',
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => ([]),
    },
    scroll: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    duration: {
      type: Number,
      default: 500,
    },
    sticky: {
      type: Boolean,
      default: false,
    },
    top: {
      type: String,
      default: '0',
    },
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
    height: {
      type: Number,
      default: 600,
    },
    errors: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      isMounted: false,
      refs: null,
      scrollPos: 0,
      itemWidth: 0,
      itemOffset: 0,
      contentHeight: 0,
    };
  },
  computed: {
    styles() {
      return {
        '--base-steps-position': this.sticky ? 'sticky' : 'relative',
        '--base-steps-top': this.top || '0',
        '--column-count': this.items.length * 2 - 1,
        '--step-count': this.items.length,
        '--translateX': `${100 * -this.currentPos}%`,
        '--active-item-width': `${this.activeItemWidth}px`,
        '--active-item-offset': `${this.activeItemOffset}px`,
        '--scroll-position': `translateX(${this.scrollPos}px)`,
        '--scroll-duration': `${this.duration}ms`,
        '--content-height': `${this.contentHeight}px`,
        '--max-content-height': `${this.height}px`,
      };
    },
    activeHeight() {
      return Object.keys(this.refs).length ? this.refs[this.value][1].scrollHeight : this.contentHeight;
    },
    classes() {
      return {
        'base-steps': true,
      };
    },
    currentPos() {
      return this.items.findIndex((x) => x.value === this.value);
    },
    currentStep() {
      return this.items[this.items.findIndex((x) => x.value === this.value)].value;
    },
    lastStep() {
      return this.items[this.items.length - 1].value === this.value;
    },
    nextStep() {
      const next = this.items.findIndex((x) => x.value === this.value) + 1;

      return this.items[this.clamp(next, 0, this.items.length - 1)].value;
    },
    activeItemWidth: {
      get() {
        return this.itemWidth;
      },
      set(val) {
        this.itemWidth = val;
      },
    },
    activeItemOffset: {
      get() {
        return this.itemOffset;
      },
      set(val) {
        this.itemOffset = val;
      },
    },
  },
  mounted() {
    this.refs = this.$refs;

    setTimeout(() => {
      this.contentHeight = this.refs[this.value][1].scrollHeight;
    }, 100);

    this.isMounted = true;
  },
  updated() {
    this.$nextTick(() => {
      this.contentHeight = this.refs[this.value][1].scrollHeight;
      this.scrollTo(this.value);
    });
  },
  methods: {
    clamp(val, min, max) {
      return val < 0 ? min : val > max ? max : val;
    },
    update(val = this.value) {
      this.scrollTo(val);

      this.$emit('update', val);

      if (this.lastStep) {
        this.$emit('submit', val);
      } else {
        setTimeout(() => {
          this.contentHeight = this.refs[val][1].scrollHeight;
        }, 10);
      }
    },
    getIndex(val) {
      return this.items.findIndex((x) => x.value === val);
    },
    scrollTo(val) {
      if (this.refs?.[val]?.length) {
        const stepPos = this.refs[val][0].offsetLeft + (this.refs[val][0].offsetWidth / 2);
        const { steps } = this.refs;
        const middle = steps.offsetWidth / 2;
        const overflow = steps.offsetWidth - steps.scrollWidth;
        const result = middle - stepPos;
        const scroll = result > 0 ? 0 : result < overflow ? overflow : result;

        this.activeItemWidth = this.refs[val][0].offsetWidth;
        this.activeItemOffset = this.refs[val][0].offsetLeft;
        this.scrollPos = scroll;

        return scroll;
      }
      return 0;
    },
  },
};
</script>

<style lang="scss" scoped>
$gray: #9d9d9d;
.base-steps {
    position: var(--base-steps-position);
    top: var(--base-steps-top);
    z-index: 10;

    .divider {
        margin: 0 -15%;

        &::after {
            content: '';
            width: 100%;
            border-bottom: 1px solid $gray;
        }
    }

    .step {
        transition: background-color var(--scroll-duration) ease-in-out;
        height: 1.5rem;
        width: 1.5rem;
        display: flex;
        font-size: .8rem;
        color: var(--white);
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background-color: $gray;
        margin-right: .5rem;
    }

    &--container {
        display: grid;
        grid-template-columns: 1fr;
        padding-bottom: 1rem;
    }

    &--content {
        overflow: hidden;
        max-height: var(--max-content-height);
        transition: all var(--scroll-duration) ease-in-out;
        height: var(--content-height);
        display: grid;
        grid-template-columns: repeat(var(--step-count), 100%);

        &-holder {
            display: flex;
            flex-direction: column;
            transition: all var(--scroll-duration) ease-in-out;
            transform: translateX(var(--translateX));
            opacity: 0;
            overflow-x: auto;
            overflow-y: scroll;

            &.active {
                opacity: 1;
            }
        }

        &-item {
            padding: 0.25rem;
        }
    }

    &--holder {
        overflow: hidden;
        position: relative;
    }

    &--steps {
        transition: all var(--scroll-duration) ease-in-out;
        display: grid;
        grid-template-columns: repeat(var(--column-count), 1fr);
        transform: var(--scroll-position);
    }

    &--step {
        transition: color var(--scroll-duration) ease-in-out, background-color 250ms ease-in-out;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: .5rem 1rem;
        color: $gray;

        &.out-of-reach {
            pointer-events: none;
        }

        &:not(.divider):not(.out-of-reach) {
            cursor: pointer;
        }

        &:not(.divider):not(.out-of-reach):hover {
            background-color: var(--company-main-primary_hover);
        }

        &.active {
            color: currentColor;

            .step {
                background-color: var(--company-main-primary);
            }
        }

        &.previous {
            color: currentColor;

            .step {
                background-color: var(--info);
            }
        }

        &.error {
            color: currentColor;

            .step {
                background-color: var(--bs-danger);
            }
        }
    }

    &--slider {
        position: absolute;
        transition: all var(--scroll-duration) ease-in-out;
        width: var(--active-item-width);
        left: var(--active-item-offset);
        height: 2px;
        background-color: var(--company-main-primary);
        bottom: 0;
    }

    &--actions {
        display: flex;
        padding-top: 1rem;
    }
}
</style>
